import React, { useRef } from 'react';
import { useLabelStore } from '../../store/useLabelStore';
import { refreshGalleryUrl } from '../../utils/galleryUrl';
import { chaineFiltre, niveauxActifs, idNiveaux, paramsNiveaux, aDesReglages } from '../../utils/imageAdjust';

// Render d'un calque image : balise image simple avec object-contain.
// Le flip horizontal/vertical est géré via transform CSS (scaleX / scaleY).
//
// `el.etirer` : l'image REMPLIT son cadre, quitte à se déformer (object-fill).
// C'est le cas des cartouches que l'agent pose sous les mentions : le cadre est
// calculé pour que la zone de texte du cartouche couvre le bloc. En `contain`,
// un cartouche plus étroit que son cadre se peignait rétréci et centré, et les
// mentions débordaient sur l'illustration (rendus du 24/10 : PG/VG « mal centré »).
//
// Gomme : `el.eraseMask` est un PNG à canal alpha couvrant la BOÎTE du calque
// (opaque = conservé, transparent = effacé). On l'étire en 100% 100% pour que
// son repère colle exactement à celui dans lequel l'utilisateur a peint.
// `-webkit-mask-*` reste nécessaire pour Safari, qui ne suit pas encore la
// propriété non préfixée. Le rendu d'export raster/vectoriel passe par
// Chromium (service /print) et honore ce masque ; les chemins SVG et
// WeasyPrint reçoivent, eux, une image déjà aplatie (cf. utils/eraseMask).
export default function ImageLayer({ el }) {
    // URL déjà réémise pour ce calque. Sans ce garde-fou, une image
    // définitivement illisible relancerait une réémission à chaque échec, donc
    // en boucle : `onError` se déclenche de nouveau à chaque nouvelle source.
    const dejaTenteRef = useRef(null);

    // Une URL de galerie est SIGNÉE et n'est valable qu'une heure. Or les
    // calques sont persistés : un projet rouvert le lendemain, ou un onglet
    // resté ouvert, référence une signature morte et l'image disparaît du plan
    // de travail sans un mot. On redemande une signature fraîche, une fois, et
    // on la mémorise dans le calque.
    const onError = async () => {
        if (dejaTenteRef.current === el.url) return;
        dejaTenteRef.current = el.url;
        const fraiche = await refreshGalleryUrl(el.url);
        if (fraiche && fraiche !== el.url) {
            useLabelStore.getState().updateElement(el.id, { url: fraiche });
        }
    };

    const mask = el.eraseMask
        ? {
            maskImage: `url(${el.eraseMask})`,
            WebkitMaskImage: `url(${el.eraseMask})`,
            maskSize: '100% 100%',
            WebkitMaskSize: '100% 100%',
            maskRepeat: 'no-repeat',
            WebkitMaskRepeat: 'no-repeat',
        }
        : null;

    // Réglages d'image (flou, luminosité, contraste, niveaux, teinte, saturation).
    // Le `filter` est posé sur la BALISE IMAGE, et non sur un conteneur : c'est le
    // seul endroit où il n'affecte ni le cadre de sélection, ni les poignées, ni la
    // surface de gomme montée par-dessus.
    const filtre = chaineFiltre(el);
    const niveaux = aDesReglages(el) && niveauxActifs(el) ? paramsNiveaux(el) : null;

    const image = (
        <img
            src={el.url}
            alt={el.name || 'Calque image'}
            onError={onError}
            className={`w-full h-full ${el.etirer ? 'object-fill' : 'object-contain'} pointer-events-none`}
            style={{
                transform: `scaleX(${el.scaleX === -1 ? -1 : 1}) scaleY(${el.scaleY === -1 ? -1 : 1})`,
                ...(filtre ? { filter: filtre } : null),
                ...mask,
            }}
        />
    );

    // Le conteneur ROGNE au cadre, et il est là même sans réglage : une structure
    // qui apparaît et disparaît remonterait la balise image à chaque passage du
    // flou par zéro, ce qui la ferait recharger et clignoter. Le rognage est ce qui
    // garantit que l'écran et le PDF montrent la même chose — l'export cuit les
    // réglages dans un PNG à la forme du cadre (cf. utils/imageAdjust.js), donc un
    // flou laissé libre de déborder à l'écran ne serait tenu par aucun export.
    return (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {/* Les niveaux n'ont pas de fonction `filter` : deux passes
                `feComponentTransfer`, une affine puis une gamma. `sRGB` est
                OBLIGATOIRE — par défaut un filtre SVG travaille en linearRGB, où le
                même point noir donne une image bien plus sombre que dans un logiciel
                de retouche. Le <svg> est de taille nulle : il ne porte que la
                définition, et voyage avec le composant jusqu'à l'export. */}
            {niveaux && (
                <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
                    <defs>
                        <filter id={idNiveaux(el)} colorInterpolationFilters="sRGB">
                            <feComponentTransfer>
                                <feFuncR type="linear" slope={niveaux.pente} intercept={niveaux.decalage} />
                                <feFuncG type="linear" slope={niveaux.pente} intercept={niveaux.decalage} />
                                <feFuncB type="linear" slope={niveaux.pente} intercept={niveaux.decalage} />
                            </feComponentTransfer>
                            <feComponentTransfer>
                                <feFuncR type="gamma" exponent={niveaux.exposant} amplitude="1" offset="0" />
                                <feFuncG type="gamma" exponent={niveaux.exposant} amplitude="1" offset="0" />
                                <feFuncB type="gamma" exponent={niveaux.exposant} amplitude="1" offset="0" />
                            </feComponentTransfer>
                        </filter>
                    </defs>
                </svg>
            )}
            {image}
        </div>
    );
}
